import React, { FunctionComponent, ReactElement, lazy, Suspense } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { getAcademyMicrofrontendUrl } from "@/js/utils/getMicrofrontendUrl";
import { loadDynamicRemote } from "@/utils/loadDynamicRemote";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ShadowDOMWrapper } from "@/components/ShadowDOMWrapper";
import posthog from "posthog-js";
import { useAcademyMFEContextStore } from "@/stores/v2/academyMFEContextStore";

interface AcademyModules {
	ClassroomDrawer?: React.ComponentType<AcademyProps>;
}

export interface FeedbackData {
	rating: number;
	comment: string;
	trainingName: string;
	contentType: string;
}

interface AcademyProps {
	isOpen: boolean;
	onOpenChange: (details: { open: boolean }) => void;
	token: string;
	deviceId: string;
	masquerade: boolean;
	region: string;
	onFeedbackSubmit?: (feedback: FeedbackData) => void;
	initialTrainingId?: string;
	initialTrainingVersion?: string;
	username?: string;
	posthog?: typeof posthog;
}

interface Props {
	isOpen: boolean;
	onOpenChange: (details: { open: boolean }) => void;
}

// Fallback component for when academy MFE fails to load
const AcademyFallback = () => (
	<Box p={4}>
		<Text>Failed to load Academy content. Please try refreshing the page.</Text>
	</Box>
);

// Load Academy micro frontend dynamically, but only once.
const DynamicAcademyApp = lazy(() => {
	const academyModuleRemoteUrl = getAcademyMicrofrontendUrl(window.location.hostname) + "?nocache=" + new Date().getTime();
	return loadDynamicRemote<AcademyModules>(academyModuleRemoteUrl, "QoriaAcademy", "./QoriaAcademy", "vite", "esm")
		.then((academyModule) => {
			const component = academyModule.ClassroomDrawer;

			if (!component) {
				console.error("ClassroomDrawer component not found - Academy MFE must export ClassroomDrawer for embedding");
				return { default: AcademyFallback };
			}

			// Return a wrapper component that properly renders the academy component
			const WrappedComponent = (props: AcademyProps) => {
				try {
					// Create an isolated error boundary for the academy component
					if (component) {
						return <ErrorBoundary fallback={<AcademyFallback />}>{React.createElement(component, props)}</ErrorBoundary>;
					}
					return <AcademyFallback />;
				} catch (error) {
					console.error("Academy Wrapper: Error rendering component:", error);
					return <AcademyFallback />;
				}
			};

			return {
				default: WrappedComponent,
			};
		})
		.catch((error) => {
			console.error("Failed to load Academy MFE:", error);
			return { default: AcademyFallback };
		});
});

const handleFeedbackSubmit = (feedbackData: FeedbackData) => {
	posthog.capture("user_feedback", {
		product: "Classwize Academy",
		rating: feedbackData.rating,
		comment: feedbackData.comment,
		trainingName: feedbackData.trainingName,
		contentType: feedbackData.contentType,
	});
};

const Academy: FunctionComponent<Props> = (props): ReactElement => {
	const userSessionStore = useUserSessionStore();
	const { trainingParams, clearTrainingParams } = useAcademyMFEContextStore();
	const isMasquerade = useUserSessionStore((state) => state.masquerade);
	const { isOpen, onOpenChange } = props;

	// Local state to safely hold the initial parameters for the MFE
	const [initialParams, setInitialParams] = React.useState<{
		initialTrainingId?: string;
		initialTrainingVersion?: string;
	} | null>(null);

	// When a trigger arrives, latch the params locally and open the drawer.
	React.useEffect(() => {
		const canOpen = trainingParams?.initialTrainingId && trainingParams?.initialTrainingVersion && !isOpen;

		if (canOpen) {
			// Latch the parameters into our stable local state
			setInitialParams({
				initialTrainingId: trainingParams.initialTrainingId,
				initialTrainingVersion: trainingParams.initialTrainingVersion,
			});

			// Perform the rest of the actions
			onOpenChange({ open: true });
			clearTrainingParams();
		}
	}, [trainingParams, isOpen, onOpenChange, clearTrainingParams]);

	// When the drawer closes, clear local state
	React.useEffect(() => {
		const isAboutToOpen = trainingParams?.initialTrainingId && trainingParams?.initialTrainingVersion;

		// Only clear the local state if the drawer is closed AND it's not about to open
		if (!isOpen && !isAboutToOpen) {
			setInitialParams(null);
		}
	}, [isOpen, trainingParams]);

	// Only load Academy MFE when drawer is open
	if (!props.isOpen) {
		return <></>;
	}

	return (
		<ErrorBoundary fallback={<Box p={4}>Failed to load Academy content</Box>}>
			<Suspense fallback={<Box p={4}>Loading Academy...</Box>}>
				<ShadowDOMWrapper emotionCacheKey="academy-shadow-dom">
					{/* 
						The 'posthog' prop passes a DIRECT REFERENCE to the same PostHog singleton 
						instance that was initialized in the parent Classwize application.
						
						What Academy MFE receives:
						- The actual PostHog object instance (not a copy or wrapper)
						- Pre-initialized with Classwize configuration (API key, host, settings)
						- Pre-identified with current user context (username, deviceId, appliance name)
						- Shared session state with parent application
						- Full PostHog API access without needing its own initialization
					*/}
					<DynamicAcademyApp
						key={initialParams?.initialTrainingId}
						isOpen={props.isOpen}
						onOpenChange={props.onOpenChange}
						token={userSessionStore.getToken() || ""}
						deviceId={userSessionStore.getActiveDevice() || ""}
						region={userSessionStore.deviceinfo?.region as string}
						masquerade={isMasquerade}
						onFeedbackSubmit={handleFeedbackSubmit}
						initialTrainingId={initialParams?.initialTrainingId}
						initialTrainingVersion={initialParams?.initialTrainingVersion}
						username={userSessionStore.getActiveUser()}
						posthog={posthog} // Pass PostHog singleton instance reference
					/>
				</ShadowDOMWrapper>
			</Suspense>
		</ErrorBoundary>
	);
};

export default Academy;
